import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, CheckCircle2, FileText, Download, ArrowRight, Calendar } from "lucide-react";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import FloatingBookCTA from "@/components/site/FloatingBookCTA";
import LeadCaptureForm from "@/components/lead/LeadCaptureForm";
import { submitChecklist } from "@/lib/lead-api";

const SECTIONS = [
  {
    title: "Decide & timeline",
    items: [
      "Pick your target move date — be specific (your 'flag in the sand')",
      "Work backwards: consulate appointment, document expiry, packing, sale of UK home",
      "Identify which UK consulate you'll apply at — London, Manchester or Edinburgh",
    ],
  },
  {
    title: "Financial proof",
    items: [
      "Bank statements (last 12 months)",
      "Pension statements — state, private and any drawdown",
      "Completion statement if you've recently sold a property",
      "Proof you're no longer working (NLV requirement)",
      "Apostilled and sworn translations for any official documents",
    ],
  },
  {
    title: "Health cover",
    items: [
      "If state pension age or eligible — apply for the S1 form",
      "Otherwise: get a private health insurance quote (12 months prepaid)",
      "Register the S1 with Spanish authorities before submitting your NLV",
    ],
  },
  {
    title: "Key documents (with shelf-lives)",
    items: [
      "ACRO police certificate — valid 6 months from issue",
      "Medical letter with specific Spanish-required wording — valid 3 months",
      "Marriage certificate (if applying as a couple)",
      "Apostille each of the above (FCDO Legalisation Office)",
      "Sworn translation of each into Spanish",
    ],
  },
  {
    title: "Submit & wait",
    items: [
      "Complete the three Spanish visa forms",
      "Book your consulate appointment via BLS",
      "Bring printed copies of everything plus your passport + photos",
      "Pay the visa fee",
      "Wait — usually 2 weeks to 3 months depending on consulate and workloads",
    ],
  },
  {
    title: "On arrival in Spain",
    items: [
      "Register on the padrón (local town hall)",
      "Book your fingerprint appointment",
      "Collect your residency card (TIE)",
      "Track 183-day rule — when you become Spanish tax-resident",
      "Renewal: start 60 days before expiry",
    ],
  },
];

export default function Checklist() {
  const [unlocked, setUnlocked] = useState(false);
  const [name, setName] = useState("");

  const handleSubmit = async (n, email, consent) => {
    await submitChecklist({ name: n, email, consent_marketing: consent, source: "/checklist" });
    setName(n);
    setUnlocked(true);
    // Smooth scroll to the checklist content
    setTimeout(() => {
      const el = document.getElementById("checklist-content");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
  };

  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      <SiteNav />

      {/* HERO + EMAIL GATE */}
      {!unlocked && (
        <section className="pt-28 pb-20 lg:pt-32 lg:pb-24 editorial relative overflow-hidden">
          <div className="absolute -top-32 -right-40 w-[600px] h-[600px] rounded-full bg-[var(--terracotta)]/10 blur-3xl pointer-events-none" />
          <div className="relative max-w-5xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <Link to="/" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--terracotta)]" data-testid="checklist-back">
                <ArrowLeft className="w-4 h-4" /> Back to home
              </Link>
              <div className="mt-8 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--cream-deep)] border border-[var(--terracotta)]/20 text-xs uppercase tracking-[0.2em] text-[var(--terracotta-deep)] font-medium">
                <FileText className="w-3.5 h-3.5" /> Free checklist · 1 page
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--navy)] mt-6 leading-[1.04]" data-testid="checklist-headline">
                The complete <em className="text-[var(--terracotta)]">NLV checklist.</em>
              </h1>
              <p className="mt-6 text-lg text-[var(--ink-soft)] leading-relaxed max-w-xl">
                Every document, deadline and form in one place — built from hundreds of real
                applications Carley has personally guided. Print it, tick it off, never miss a step.
              </p>
              <ul className="mt-8 space-y-3 text-[var(--ink-soft)]">
                {["Every document with its expiry date — so nothing runs out before you submit", "Stage-by-stage from the very beginning to holding your TIE card", "Carley's print-friendly layout"].map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[var(--terracotta)] flex-shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl bg-white p-7 lg:p-8 shadow-2xl border border-black/5">
                <h2 className="font-serif text-2xl text-[var(--navy)]">Get the checklist</h2>
                <p className="mt-2 text-sm text-[var(--ink-soft)]">
                  Reveals instantly on the next screen — and Carley will email you a copy you can save.
                </p>
                <div className="mt-6">
                  <LeadCaptureForm
                    onSubmit={handleSubmit}
                    ctaLabel="Show me the checklist"
                    fbEvent="Lead"
                    fbEventParams={{ content_name: "NLV Readiness Checklist", source: "/checklist" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* UNLOCKED CHECKLIST */}
      {unlocked && (
        <section id="checklist-content" className="pt-28 lg:pt-32 pb-24 editorial">
          <div className="max-w-4xl mx-auto px-6 lg:px-10">
            <div className="flex flex-wrap items-center justify-between gap-4 print:hidden">
              <div>
                <Link to="/" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--terracotta)]">
                  <ArrowLeft className="w-4 h-4" /> Back to home
                </Link>
                <div className="mt-4 text-xs uppercase tracking-[0.25em] text-[var(--terracotta)] font-medium">
                  Thanks {name.split(" ")[0]} — here's your checklist
                </div>
              </div>
              <button
                onClick={() => window.print()}
                className="pill inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[var(--navy)] text-[var(--cream)] font-medium"
                data-testid="checklist-print"
              >
                <Printer className="w-4 h-4" /> Print / Save as PDF
              </button>
            </div>

            <div className="mt-8 print:mt-0">
              <h1 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] leading-[1.05]">
                The <em className="text-[var(--terracotta)]">NLV Readiness</em> Checklist
              </h1>
              <div className="mt-3 text-sm text-[var(--muted)]">
                Compiled by Carley Chaplin · Get to Spain · NLV Approved
              </div>
            </div>

            <div className="mt-10 space-y-8">
              {SECTIONS.map((sec, i) => (
                <section key={i} className="rounded-2xl bg-white p-6 lg:p-8 border border-black/5 print:shadow-none print:border-[var(--ink)]/15 break-inside-avoid">
                  <div className="flex items-baseline gap-4">
                    <span className="font-serif text-3xl text-[var(--terracotta)]">{String(i + 1).padStart(2, "0")}</span>
                    <h2 className="font-serif text-2xl text-[var(--navy)]">{sec.title}</h2>
                  </div>
                  <ul className="mt-5 space-y-3">
                    {sec.items.map((it, k) => (
                      <li key={k} className="flex items-start gap-3 text-[var(--ink)]">
                        <span className="w-5 h-5 rounded border-2 border-[var(--terracotta)]/40 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span className="leading-relaxed">{it}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>

            <div className="mt-12 rounded-[2rem] p-8 lg:p-12 bg-[var(--navy)] text-[var(--cream)] print:hidden">
              <div className="text-xs uppercase tracking-[0.3em] text-[var(--gold)] font-medium">Next step</div>
              <h2 className="mt-3 font-serif text-3xl lg:text-4xl leading-tight">
                Ready to turn this checklist into a real plan?
              </h2>
              <p className="mt-4 text-[var(--cream)]/85">
                Book a call with Carley — she'll walk through your specific timeline, financial
                situation, and which documents to start with first.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href="/nlv-approved#book" className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium" data-testid="checklist-cta-book">
                  <Calendar className="w-5 h-5" /> Book my call
                </a>
                <Link to="/quiz" className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[var(--cream)]/25 text-[var(--cream)] hover:bg-[var(--cream)] hover:text-[var(--navy)] font-medium">
                  Take the readiness quiz <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      <SiteFooter />
      <FloatingBookCTA />

      <style>{`
        @media print {
          header, footer, [data-testid="floating-book-cta"] { display: none !important; }
          body { background: white !important; }
          .grain::before { display: none !important; }
        }
      `}</style>
    </main>
  );
}
